import HTTPLoader from './http_loader';
import _ from './underscore';
import _Logger from './logger';

const Logger = new _Logger('XHR');

export type IHttpParams = {[key: string]: string | string[] | number | number[] | boolean};

export type IHttpHeaders = {[key: string]: string};

export interface IXHRSuccessResponse {
    status: number;
    data: string;
    xhr: XMLHttpRequest;
}

export interface IXHRErrorResponse {
    status: number;
    statusText: string;
    data: any;
}

interface IPromiseCallbacks {
    resolve: (arg: IXHRSuccessResponse) => void;
    reject: (arg: IXHRErrorResponse) => void;
}

/**
 * XHR handles all the requests to the server from the api, low level only.
 *
 * Usage:
 *
 * When we create new instance of XHR class we create an XMLHttpRequest property
 * and set default params and headers.
 *
 * Blow are few examples of how we can use this XHR object:
 *
 * Send GET request with custome header and data as params.
 *
 *   new XHR()
 *   .setHeader({ 'X-888-GET': 'TEST' })
 *   .setParams({'a':'bb','b':'aa'})
 *   .get('/lobby');
 *
 * Send POST request with custom header and data as payload.
 *
 *   new XHR()
 *   .setHeader({ 'X-888-POST': 'TEST' })
 *   .setParams({'a':'bb','b':'aa'})
 *   .post(path);
 *
 * You can add or remove the set functions because they are chainable methods.
 * The method return a promise with a property of request that
 * contain a reference to the current xhr.
 *
 *
 */
export default class XHR {
    /**
     * @static
     * @type {number}
     */
    static HTTP_OK_STATUSES = [200, 204];

    static QUERY_MODE = 'BUILD_QUERY_STRING';

    static JSON_MODE = 'SEND_JSON';

    static getEncodedParams(key: string, val: string) {
        return encodeURIComponent(key) + '=' + encodeURIComponent(val);
    }

    /**
     * Create encoded query string
     * @param xhrParams
     * @returns {string | null}
     */
    static getQueryString(xhrParams: IHttpParams) {
        const params: string[] = [];
        if (xhrParams) {
            Object.keys(xhrParams).forEach((key) => {
                if (_.isArray(xhrParams[key])) {
                    (xhrParams[key] as string[]).forEach(val => params.push(XHR.getEncodedParams(key, val)));
                } else {
                    params.push(XHR.getEncodedParams(key, xhrParams[key] as string));
                }
            });

            return params.join('&');
        }
        return null;
    }

    private _xhr: XMLHttpRequest;

    private _mode: string;

    private _headers: IHttpHeaders;

    private _params: IHttpParams;

    /**
     * Sets default params and initiates XHR on the browser to a local var.
     *
     * @constructor
     */
    constructor() {
        this._headers = {};
        this._params = {};
        this._mode = XHR.QUERY_MODE;
        this._xhr = new XMLHttpRequest();
    }


    /**
     * Set the new params to the default params property.
     *
     *
     * @param {Object} params to set.
     * @returns {XHR} instance of an this currently used HTTP. For chaining.
     * @public
     */
    setParams(params: IHttpParams): XHR {
        Object.keys(params).forEach((key) => {
            this._params[key] = params[key];
        });

        Logger.debug('XHR::CURRENT PARAMS', this._params);

        return this;
    }


    setParamsMode(mode: string): XHR {
        if (mode !== this._mode) {
            this._mode = mode;
        }

        return this;
    }


    /**
     * Returns the params list set on this class instance.
     *
     *
     * @returns {Object} of the stored params on this instance.
     * @public
     */
    getParams(): IHttpParams {
        return this._params;
    }


    /**
     * Change the params object and order it as a valid form to send data as xhr request.
     *
     *
     * @returns {string} of the query that will be requested from the server.
     * @public
     */
    getHTTPBuildQuery() {
        const xhrParams = this.getParams();
        return XHR.getQueryString(xhrParams);
    }


    /**
     * Get a customize headers and set them to _header property.
     * The headers will added to the XHR request. (chain method).
     *
     *
     * @param {Object} headers - key => value object of headers you would like to set for this request.
     * @returns {XHR} instance of an this currently used XHR. For chaining.
     * @public
     */
    setHeaders(headers: IHttpHeaders): XHR {
        Object.keys(headers).forEach((key) => {
            this._headers[key] = headers[key];
        });

        Logger.debug('XHR::CURRENT HEADERS', this._headers);

        return this;
    }


    /**
     * Returns a list of headers
     *
     *
     * @returns {Object} of the stored headers on this instance.
     * @public
     */
    getHeaders(): IHttpHeaders {
        return this._headers;
    }


    /**
     * Add the customize headers to the current xhr request.
     *
     *
     * @returns {XHR} instance of an this currently used XHR. For chaining.
     * @public
     */
    headerHandler(): XHR {
        const headers = this.getHeaders();

        if (headers) {
            Object.keys(headers).forEach((key) => {
                this._xhr.setRequestHeader(key, headers[key]);
            });
        }

        return this;
    }


    /**
     * Performs a GET request. Encapsulates .request from outside.
     *
     *
     * @param {string} url - The path to GET the data from.
     * @returns {Promise} Thenable/Catcheable - A useable callback what to do when the request is back.
     * @public
     */
    get(url: string): Promise<IXHRSuccessResponse> {
        return this.request('GET', url);
    }

    /**
     * Send POST request.
     *
     *
     * @param {string} url - The path to POST the data to.
     * @returns {Promise} Thenable/Catcheable - A usable callback what to do when the request is back.
     * @public
     */
    post(url: string): Promise<IXHRSuccessResponse> {
        return this.request('POST', url);
    }

    /**
     * Getter for the XHR response strctured data we use internally to map XHR.
     *
     * @returns {Object} XHR response object.
     * @public
     */
    private _getXHRResponse() {
        return {
            response: this._xhr.response,
            readyState: this._xhr.readyState,
            status: this._xhr.status
        };
    }

    /**
     * Send reject promise when promise is resolved on success and on error.
     *
     *
     * @param {Promise} promise - Where to resolve the promise. A context to use.
     * @public
     */
    onReadyState(promise: IPromiseCallbacks): void {
        const xhrResponse = this._getXHRResponse();

        if (4 === xhrResponse.readyState) {
            if (XHR.HTTP_OK_STATUSES.includes(xhrResponse.status)) {
                this.onSuccess(promise.resolve);
            } else {
                this.onError(promise.reject);
            }
        }
    }


    /**
     * Send reject promise when error occurred.
     *
     *
     * @param {Promise} reject - A pointer to the REJECT Promise when something goes wrong.
     * @public
     */
    onError(reject: (errorResponse: IXHRErrorResponse) => void) {
        const errorResponse = {
            status: this._xhr.status,
            statusText: this._xhr.statusText,
            data: this._xhr.response
        };

        HTTPLoader.hide();
        reject(errorResponse);
    }


    /**
     * This method handles the success of XHR and actually executes the callback;
     *
     *
     * @param {Promise} success - A pointer to the SUCCESS Promise when a valid HTTP request is returned.
     * @public
     */
    onSuccess(success: (arg: IXHRSuccessResponse) => void) {
        const successResponse: IXHRSuccessResponse = {
            status: this._xhr.status,
            data: this._xhr.responseText,
            xhr: this._xhr
        };

        HTTPLoader.hide();
        success(successResponse);
    }


    /**
     * Prepare all your setting request and send the request to the server.
     *
     *
     * @param {string} method - type of HTTP a action you would like to perform: GET, POST, PUT, DELETE, etc.
     * @param {string} url - that you would like to get request.
     * @returns {Promise} Thenable/Catcheable.
     * @public
     */
    request(method: string, url: string): Promise<IXHRSuccessResponse> {
        HTTPLoader.show();
        const requestPromise = new Promise<IXHRSuccessResponse>((resolve, reject) => {
            const promiseCallbacks: IPromiseCallbacks = {
                resolve: resolve,
                reject: reject
            };

            this._xhr.open(method, url);

            // Add customized headers.
            this.headerHandler();

            // event will call on xhr state than send reject or resolve promise.
            this._xhr.onreadystatechange = () => this.onReadyState(promiseCallbacks);

            // event will call on http exceptions than send reject promise.
            this._xhr.onerror = () => this.onError(reject);

            // Fire the request to server but before that enter to getHTTPBuildQuery to check for more data to send.
            switch(this._mode) {
                case XHR.JSON_MODE:
                    this._xhr.send(JSON.stringify(this._params));
                    break;
                default:
                    this._xhr.send(this.getHTTPBuildQuery());
                    break;
            }
            // this._xhr.send(this.getHTTPBuildQuery());
        });

        // Add the xhr to be property of the request promise , help in test to contain the reference.
        (requestPromise as any)._xhr = this._xhr;

        return requestPromise;
    }
}
